import React from "react"
import { useTranslation } from "react-i18next"

import { If } from "@/components/if/if"
import { Page } from "@/components/page"
import { PageCard } from "@/components/page-card"
import { FONT_WEIGHT } from "@/models/typography"
import { HStack } from "@/nzds/layout"
import { useEntities } from "@/services/entities"
import { useFlags } from "@/services/feature"
import { APP_NAME } from "@/utils/constants"

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import {
  Box,
  Button,
  Divider,
  Fade,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/material"

import { UploadStep } from "../../components/upload-step"
import { useUploadEntities } from "../../services/use-upload-entities"
import { useUploadIntervalUsage } from "../../services/use-upload-interval-usage"
import { useUploadMeters } from "../../services/use-upload-meters"
import { useUploadUsage } from "../../services/use-upload-usage"
import { useUploadUsers } from "../../services/use-upload-users"

interface UploadStep {
  content: React.ReactNode
  name: string
}

export const DataUploadRoute: React.FC = () => {
  const { t } = useTranslation("data")
  const flags = useFlags()
  const { entitiesHasData, entitiesIsFetched } = useEntities()
  const isBillPdfUploadEnabled = Boolean(flags.data_manager_bill_pdf_upload)
  const [activeStepIndex, setActiveStepIndex] = React.useState(0)
  const isUsageAndEmissionsEnabled = entitiesIsFetched && entitiesHasData

  const building_template_url =
    "https://docs.google.com/spreadsheets/d/1XZj5TGuaskyTzHzvcWdheWZamLMNnGm_BggMUnuE9GE/export?format=xlsx"

  const meter_template_url =
    "https://docs.google.com/spreadsheets/d/1NR8MekctYbwZJq38cTiQ83tRiCxCT2sXQA_izE2a-R4/export?format=xlsx"
  const usage_template_url =
    "https://docs.google.com/spreadsheets/d/1qMgLRQoOlcrDE8pJJtLfd6b8ooxWJCGI6cmgNCxH0dw/export?format=xlsx"
  const interval_usage_template_url =
    "https://docs.google.com/spreadsheets/d/1o5ourogOmmzoXWpGOPE7KTSk8QpVjswTrxUYxIpCyNQ/export?format=xlsx"
  const user_data_template_url =
    "https://docs.google.com/spreadsheets/d/1lD4ZJKlH5Vau84jrJiqZIEZ8nwZ-E4g9AnLnh8jJi0A/export?format=xlsx"

  const buildingDataStep: UploadStep = {
    name: t("upload.buildingData.title"),
    content: (
      <UploadStep
        downloadTemplateButton={
          <Button
            color="secondary"
            component="a"
            download
            href={building_template_url}
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            variant="contained"
          >
            {t("upload.buildingData.downloadTemplateButton")}
          </Button>
        }
        downloadTemplateDescription={t(
          "upload.buildingData.downloadTemplateDescription"
        )}
        successMessage={t("upload.buildingData.successMessage")}
        type="building data"
        uploadCsvButtonText={t("upload.buildingData.uploadCsvButton")}
        uploadDescription={t("upload.buildingData.uploadCsvDescription")}
        uploadErrorDescription={t("upload.buildingData.uploadErrorDescription")}
        uploadErrorTitle={t("upload.buildingData.uploadErrorTitle")}
        uploadQuery={useUploadEntities}
      />
    ),
  }

  const meterDataStep: UploadStep = {
    name: t("upload.meterData.title"),
    content: (
      <UploadStep
        downloadTemplateButton={
          <Button
            color="secondary"
            component="a"
            disabled={!isUsageAndEmissionsEnabled}
            download
            href={meter_template_url}
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            variant="contained"
          >
            {t("upload.meterData.downloadTemplateButton")}
          </Button>
        }
        downloadTemplateDescription={t(
          "upload.meterData.downloadTemplateDescription"
        )}
        successMessage={t("upload.meterData.successMessage")}
        type="meter data"
        uploadCsvButtonText={t("upload.meterData.uploadCsvButton")}
        uploadDescription={t("upload.meterData.uploadCsvDescription")}
        uploadErrorDescription={t("upload.meterData.uploadErrorDescription")}
        uploadErrorTitle={t("upload.meterData.uploadErrorTitle")}
        uploadQuery={useUploadMeters}
      />
    ),
  }

  const usageDataStep: UploadStep = {
    name: t("upload.usageData.title"),
    content: (
      <UploadStep
        downloadTemplateButton={
          <Button
            color="secondary"
            component="a"
            disabled={!isUsageAndEmissionsEnabled}
            download
            href={usage_template_url}
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            variant="contained"
          >
            {t("upload.usageData.downloadTemplateButton")}
          </Button>
        }
        downloadTemplateDescription={t(
          "upload.usageData.downloadTemplateDescription"
        )}
        successMessage={t("upload.usageData.successMessage")}
        type="usage data"
        uploadCsvButtonText={
          isBillPdfUploadEnabled
            ? t("upload.usageData.uploadCsvOrPdfButton")
            : t("upload.usageData.uploadCsvButton")
        }
        uploadDescription={
          isBillPdfUploadEnabled
            ? t("upload.usageData.uploadCsvOrPdfDescription")
            : t("upload.usageData.uploadCsvDescription")
        }
        uploadErrorDescription={t("upload.usageData.uploadErrorDescription")}
        uploadErrorTitle={t("upload.usageData.uploadErrorTitle")}
        uploadQuery={useUploadUsage}
      />
    ),
  }

  const intervalUsageDataStep: UploadStep = {
    name: t("upload.intervalUsageData.title"),
    content: (
      <UploadStep
        downloadTemplateButton={
          <Button
            color="secondary"
            component="a"
            disabled={!isUsageAndEmissionsEnabled}
            download
            href={interval_usage_template_url}
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            variant="contained"
          >
            {t("upload.intervalUsageData.downloadTemplateButton")}
          </Button>
        }
        downloadTemplateDescription={t(
          "upload.intervalUsageData.downloadTemplateDescription"
        )}
        successMessage={t("upload.intervalUsageData.successMessage")}
        type="interval usage data"
        uploadCsvButtonText={t("upload.intervalUsageData.uploadCsvButton")}
        uploadDescription={t("upload.intervalUsageData.uploadCsvDescription")}
        uploadErrorDescription={t(
          "upload.intervalUsageData.uploadErrorDescription"
        )}
        uploadErrorTitle={t("upload.intervalUsageData.uploadErrorTitle")}
        uploadQuery={useUploadIntervalUsage}
      />
    ),
  }

  const userDataStep: UploadStep = {
    name: t("upload.userData.title"),
    content: (
      <UploadStep
        downloadTemplateButton={
          <Button
            color="secondary"
            component="a"
            disabled={!isUsageAndEmissionsEnabled}
            download
            href={user_data_template_url}
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            variant="contained"
          >
            {t("upload.userData.downloadTemplateButton")}
          </Button>
        }
        downloadTemplateDescription={t(
          "upload.userData.downloadTemplateDescription"
        )}
        successMessage={t("upload.userData.successMessage")}
        type="user data"
        uploadCsvButtonText={t("upload.userData.uploadCsvButton")}
        uploadDescription={t("upload.userData.uploadCsvDescription")}
        uploadErrorDescription={t("upload.userData.uploadErrorDescription")}
        uploadErrorTitle={t("upload.userData.uploadErrorTitle")}
        uploadQuery={useUploadUsers}
      />
    ),
  }

  const steps: UploadStep[] = [
    buildingDataStep,
    meterDataStep,
    usageDataStep,
    intervalUsageDataStep,
    userDataStep,
  ]

  const handleBackClick = (): void => {
    setActiveStepIndex((prevActiveStepIndex) => prevActiveStepIndex - 1)
  }

  const handleNextClick = (): void => {
    setActiveStepIndex((prevActiveStepIndex) => prevActiveStepIndex + 1)
  }

  const isStepEnabled = (step: UploadStep): boolean => {
    return steps.indexOf(step) === 0 || isUsageAndEmissionsEnabled
  }

  return (
    <Page>
      <PageCard>
        <Stack
          sx={{
            gap: 0.5,
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: FONT_WEIGHT.bold,
            }}
          >
            {t("upload.title")}
          </Typography>
          <Typography>
            {t("upload.description", { appName: APP_NAME })}
          </Typography>
        </Stack>
        <Divider sx={(theme) => ({ margin: `${theme.spacing(2)} 0` })} />
        <Box>
          <Stepper
            activeStep={activeStepIndex}
            data-testid="stepper"
            sx={{ maxWidth: 720, mb: 4, mt: 3 }}
          >
            {steps.map((step) => (
              <Step key={step.name} completed={false}>
                <StepButton
                  disabled={!isStepEnabled(step)}
                  disableRipple={true}
                  onClick={() => {
                    setActiveStepIndex(steps.indexOf(step))
                  }}
                >
                  {step.name}
                </StepButton>
              </Step>
            ))}
          </Stepper>
          <Box
            sx={{
              maxWidth: 1400,
              mx: 2,
            }}
          >
            {steps.map((step, index) => {
              return (
                <If key={step.name} condition={activeStepIndex === index}>
                  <div>{step.content}</div>
                </If>
              )
            })}
            <HStack sx={{ gap: 2, justifyContent: "flex-end", mt: 2 }}>
              <If condition={activeStepIndex !== 0}>
                <Fade in={true}>
                  <Button
                    disabled={!isUsageAndEmissionsEnabled}
                    onClick={handleBackClick}
                    variant="outlined"
                  >
                    {t("button.back", { ns: "common" })}
                  </Button>
                </Fade>
              </If>
              <If condition={activeStepIndex !== steps.length - 1}>
                <Fade in={true}>
                  <Button
                    disabled={!isUsageAndEmissionsEnabled}
                    onClick={handleNextClick}
                    variant="contained"
                  >
                    {t("button.next", { ns: "common" })}
                  </Button>
                </Fade>
              </If>
            </HStack>
          </Box>
        </Box>
      </PageCard>
    </Page>
  )
}
